import { HTMLAttributes } from 'react'

import Container from '@/components/base/container'
import CtaButton from '@/components/base/cta-button'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { manifestoRevealAnimation } from '@/components/sections/animations/manifesto'
import Spacer from '@/components/sections/spacer'

import { cn } from '@/lib/utils'
import { WPImage, WPLink } from '@/types'

type ManifestoProps = HTMLAttributes<HTMLDivElement> & {
  background?: 'light' | 'dark'
  title_1?: string
  title_2?: string
  text?: string
  image?: WPImage
  link?: WPLink
}

const Manifesto = ({
  background,
  title_1,
  title_2,
  text,
  image,
  link,
  className,
  ...props
}: ManifestoProps) => {
  return (
    <Motion animation={manifestoRevealAnimation}>
      {(motionRef) => (
        <Section
          className="w-screen overflow-hidden"
          ref={motionRef}
          {...props}
        >
          <div
            className={cn(
              background === 'dark'
                ? 'bg-background-frame-dark text-text-primary-dark'
                : 'bg-transparent text-text-primary-light',
              className
            )}
          >
            <Spacer variant="xxl" />
            <div className="px-padding-sm">
              <div className="flex flex-col gap-spacing-xs md:gap-spacing-md">
                <div
                  className={cn(
                    'flex items-center gap-spacing-2xs place-self-end md:gap-spacing-sm',
                    background === 'dark'
                      ? 'text-text-heading-dark'
                      : 'text-text-heading-light'
                  )}
                  data-motion-line-1
                >
                  {title_1?.includes('[IMG]') ? (
                    <>
                      <Typography variant="big" className="whitespace-nowrap">
                        {title_1.split('[IMG]')[0]}
                      </Typography>
                      <div className="aspect-square size-[42px] flex-[0_0_42px] overflow-hidden md:size-[182px] md:flex-[0_0_182px]">
                        <img
                          src={image?.sizes?.medium?.url || image?.url}
                          alt={image?.alt}
                          className="h-full w-full object-cover object-center"
                          data-motion-image
                        />
                      </div>
                      <Typography variant="big" className="whitespace-nowrap">
                        {title_1.split('[IMG]')[1]}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="big">{title_1}</Typography>
                  )}
                </div>
                <div
                  className={cn(
                    'flex items-center gap-spacing-2xs place-self-start md:gap-spacing-sm',
                    background === 'dark'
                      ? 'text-text-heading-dark'
                      : 'text-text-heading-light'
                  )}
                  data-motion-line-2
                >
                  {title_2?.includes('[IMG]') ? (
                    <>
                      <Typography variant="big">
                        {title_2.split('[IMG]')[0]}
                      </Typography>
                      <div className="aspect-square size-[182px] flex-[0_0_182px]">
                        <img
                          src={image?.url}
                          alt={image?.alt}
                          className="h-full w-full object-cover object-center"
                        />
                      </div>
                      <Typography variant="big">
                        {title_2.split('[IMG]')[1]}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="big">{title_2}</Typography>
                  )}
                </div>
              </div>
            </div>
            <Spacer variant="xxl" />
            <Container>
              <Grid>
                <div className="md:col-start-0 col-span-2 flex flex-col gap-d-spacer-sm md:col-span-6 lg:col-span-7 lg:col-start-6">
                  <Typography
                    data-motion-text
                    variant="regular"
                    className={cn(
                      background === 'dark'
                        ? 'text-text-primary-dark'
                        : 'text-text-primary-light'
                    )}
                  >
                    {text}
                  </Typography>
                  {link?.url ? (
                    <CtaButton
                      href={link?.url}
                      target={link?.target}
                      variant="secondary"
                      className="place-self-start"
                      data-motion-cta
                    >
                      {link?.title}
                    </CtaButton>
                  ) : null}
                </div>
              </Grid>
            </Container>
            <Spacer variant="xxl" />
          </div>
        </Section>
      )}
    </Motion>
  )
}

export default Manifesto
